import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

const VERSION_JSON_PATH = '/version.json';

// version.json written by an older deploy has none of the release fields. Such a build is
// treated as breaking (reload required, no notes) — the behaviour from before release notes.
export function normalizeRelease(data) {
  const prs = Array.isArray(data?.prs)
    ? data.prs.filter((pr) => pr && typeof pr.url === 'string' && typeof pr.title === 'string')
    : [];
  return {
    breaking: data?.breaking !== false,
    notes: typeof data?.notes === 'string' ? data.notes : '',
    notesMode: data?.notesMode === 'pr' ? 'pr' : 'release',
    prs,
  };
}

export const useAppVersionStore = defineStore('appVersion', () => {
  // Версия JS, реально загруженного в текущую страницу — вшита в бандл на этапе
  // билда (define в vite.config.js, тот же resolveBuildInfo(), что пишет version.json).
  // Раньше она угадывалась первым фетчем /version.json, и устаревший ответ CDN
  // после деплоя фиксировал старый baseline → ложные повторные диалоги (WP-1013).
  const loadedVersion = ref(__APP_VERSION__);
  const loadedBuildTime = __APP_BUILD_TIME__;

  // Последняя версия, увиденная с сервера. Совпадает с loadedVersion, пока
  // на сервере не появился новый билд.
  const latestVersion = ref(null);

  const isNewVersionAvailable = ref(false);

  // Release metadata of the newer build (valid while isNewVersionAvailable). Refreshed on
  // every poll that still sees a newer build, so a later deploy can upgrade a non-breaking
  // notice to a required reload.
  const isBreaking = ref(true);
  const notes = ref('');
  const notesMode = ref('release');
  const prs = ref([]);

  // Breaking is sticky: this tab skipped any breaking build deployed since it loaded, even if
  // a later non-breaking build is now the latest.
  let sawBreaking = false;

  function setRelease(release) {
    sawBreaking = sawBreaking || release.breaking;
    isBreaking.value = sawBreaking;
    notes.value = release.notes;
    notesMode.value = release.notesMode;
    prs.value = release.prs;
  }

  // A newer build that open tabs must reload into (drives the modal and the router's hard
  // navigation). A non-breaking build keeps the tab on its old, still-retained chunks.
  const requiresReload = computed(() => isNewVersionAvailable.value && isBreaking.value);

  function setLatestVersion(version) {
    latestVersion.value = version;
  }

  function markNewVersionAvailable() {
    isNewVersionAvailable.value = true;
  }

  function dismiss() {
    isNewVersionAvailable.value = false;
  }

  async function fetchVersion() {
    if (import.meta.env.DEV) {
      return null;
    }

    try {
      const cacheBuster = Date.now();
      const response = await fetch(`${VERSION_JSON_PATH}?t=${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return { version: data.version, buildTime: data.buildTime, release: normalizeRelease(data) };
    } catch (error) {
      console.warn('Version check failed:', error.message);
      return null;
    }
  }

  // Делает запрос /version.json и обновляет состояние стора.
  // Возвращает true, если на сервере билд новее, чем загружен в текущую страницу.
  async function checkVersion() {
    const fetched = await fetchVersion();
    if (!fetched?.version) {
      return false;
    }

    setLatestVersion(fetched.version);

    if (fetched.version === loadedVersion.value) {
      return false;
    }

    // Устаревший edge CloudFront / S3 в окно деплоя может отдать СТАРЫЙ version.json,
    // хотя страница уже выполняет новый код. Другой SHA сам по себе — не «новая версия»:
    // новым считаем только билд со строго более поздним buildTime. Если buildTime
    // не парсится с любой стороны — откатываемся к простому сравнению версий.
    const fetchedTime = Date.parse(fetched.buildTime);
    const localTime = Date.parse(loadedBuildTime);
    if (!Number.isNaN(fetchedTime) && !Number.isNaN(localTime) && fetchedTime <= localTime) {
      return false;
    }

    setRelease(fetched.release);
    if (!isNewVersionAvailable.value) {
      console.log(`New version available: ${fetched.version} (loaded: ${loadedVersion.value})`);
      markNewVersionAvailable();
    }
    return true;
  }

  async function clearCachesAndReload() {
    if ('caches' in window) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map((name) => caches.delete(name)));
      } catch (error) {
        console.warn('Failed to clear caches:', error);
      }
    }
    window.location.reload();
  }

  return {
    loadedVersion,
    latestVersion,
    isNewVersionAvailable,
    isBreaking,
    requiresReload,
    notes,
    notesMode,
    prs,
    setLatestVersion,
    markNewVersionAvailable,
    dismiss,
    checkVersion,
    clearCachesAndReload,
  };
});
